import { NotFound } from '@components/404'
import { PdpLayout } from '@components/common'
import { ProductPrice } from '@components/common/ProductPrice/ProductPrice'
import { ProductItem } from '@components/common/ProductTile/ProductTile'
import { ProductInfo } from '@components/corra/ProductInfo'
import PaymentOptionsWidget from '@components/corra/PaymentOptionsWidget/PaymentOptionsWidget'
import { QuantitySelector } from '@components/QuantitySelector/QuantitySelector'
import { Breadcrumbs } from '@components/alfakher/pylot-breadcrumbs'
import { useAddToCart } from '@components/alfakher/pylot-cart-manager'
import { CartData } from '@corratech/pylot-cart-manager/src/use-cart'
import {
    ProductGallery,
    GalleryTypeEnum
} from '@corratech/pylot-product-gallery'
import { GiftCardProduct } from '@corratech/pylot-product-giftcard'
import { usePdpTracking } from '@components/OOKA/pylot-tag-manager'
import { Button, Container, CollapsibleContainer } from '@corratech/pylot-ui'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import VariantSelector from '@components/alfakher/pylot-variant-selector/src/VariantSelector/VariantSelector'
import { AddToWishlist } from '@components/alfakher/pylot-wishlist'
import {
    YotpoReviewsAPI,
    yotpoReviewsJsonLd,
    YotpoReviews,
    YotpoScripts,
    YotpoStarRating
} from '@components/alfakher/pylot-yotpo'
import cn from 'classnames'
import s from '@pagestyles/PDP.module.scss'
import getProduct from '@pylot-data/api/operations/get-product'
import { ProductStockStatus } from '@pylot-data/enums/ProductStockStatus.d'
import { graphqlFetch } from '@pylot-data/graphqlFetch'
import { getCMSBlocks } from '@pylot-data/hooks/cms/graphql/getCMSBlocks'
import { useCmsBlocks } from '@pylot-data/hooks/cms/use-cms-blocks'
import type { CmsBlocks } from '@pylot-data/fwrdschema'
import { useProductFetch } from '@pylot-data/hooks/product/use-product-fetch'
import {
    ButtonLabel,
    useProductUI
} from '@pylot-data/hooks/product/use-product-ui'
import {
    ConfigurableProduct,
    ConfigurableVariant,
    SimpleProduct
} from '@pylot-data/pylotschema'
import { getStoreConfig } from 'config'
import type {
    GetStaticPaths,
    GetStaticPathsContext,
    GetStaticProps,
    GetStaticPropsContext,
    InferGetStaticPropsType
} from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import React, {
    ReactElement,
    useState,
    useEffect,
    useRef,
    useMemo
} from 'react'
import { ProductTierPrice } from '@components/ProductTierPrice/ProductTierPrice'
import { RichContent } from '@corratech/pylot-rich-content'
import type { Maybe, MediaGalleryInterface } from '@pylot-data/pylotschema'
import { MagePlazaProductBadge } from '@corratech/pylot-mageplaza-badges'
import slickStyles from '@commonStyles/Slick.module.scss'
import { SlickprevCircle, SlicknextCircle } from '@components/icons'
import { ReadMore } from '@components/Readmore/ReadMore'
import { SEO, SEO_ENUMS } from '@components/seo'
import Image from '@corratech/pylot-image'
import { useAlfakherContext } from 'components/alfakherContext/AlfakherContext'
import { BundleProductSelector } from '@components/BundleProduct/BundleProductSelector'
import MultiBuyProducts from 'stores/ooka_default/components/MultiBuyProducts/MultiBuyProducts'
import { Upsell } from 'stores/ooka_default/components/alfakher/pylot-upsell/src/Upsell'
import { KlaviyoViewedProductTracker } from '@components/Klaviyo/src/KlaviyoViewedProductTracker'
import { useIntersection } from '@components/AddToCartPopup/hooks/useInterSection'
import { ShareIcon } from 'stores/ooka_default/components/icons'
import { useIsMobile } from '@components/common/IsMobile/useIsMobile'
import { CrossShell } from 'stores/ooka_default/components/alfakher/pylot-cross-shell/src/CrossShell'
import { CustomKitLayout } from 'stores/ooka_uae_store/airglobal/pylot-custom-kit-flow/src/Components/CustomKitLayout'
import {
    buildAddToCartItemFromProductVariant,
    trackAddedToCartFromItems
} from '@components/Klaviyo/src/KlaviyoAddToCartTracker'
import { useUser } from '@corratech/pylot-auth-manager'
import { useIsArabic } from '@components/common/IsArabic/useIsArabic'

type AddToCartItems = {
    sku: string
    parent_sku?: string
    selected_options?: Maybe<string>[]
    quantity: number
    end_type?: string | null
    billing_period?: string | null
}[]
export const MD_INFINITE = 'md_end_infinite'
const PDP_RETURN_POLICY = 'pdp-return-policy'
export const getStaticPaths: GetStaticPaths = async (
    context: GetStaticPathsContext
) => {
    const locales = context.locales ?? []
    try {
        const paths: {
            params: { product_url: string | undefined }
            locale: string
        }[] =
            (await locales?.reduce(
                async (
                    acc: Promise<
                        {
                            params: { product_url: string | undefined }
                            locale: string
                        }[]
                    >,
                    locale: string
                ) => {
                    if (locale !== 'default') {
                        const config = getStoreConfig(locale) // Pass locale as an argument
                        const productSearch = await graphqlFetch<
                            Record<string, never>,
                            { products: { items: { url_key: string }[] } }
                        >({
                            query: /* GraphQL */ `
                  {
                    products(
                      pageSize: ${config.static.products.count}
                      currentPage: 1
                      search: ""
                    ) {
                      items {
                        url_key
                      }
                    }
                  }
                `
                        })
                        const productsByLocale = productSearch.data.products.items.map(
                            ({ url_key }) => {
                                return {
                                    params: { product_url: url_key },
                                    locale
                                }
                            }
                        )
                        return [...(await acc), ...productsByLocale]
                    }
                    return await acc
                },
                Promise.resolve([])
            )) ?? []
        return {
            // Automatically generate paths for all the products
            paths,
            fallback: 'blocking'
        }
    } catch (e) {
        console.error('PDP GETSTATICPATHS ERROR:', e)
        return {
            paths: [],
            fallback: 'blocking'
        }
    }
}
export const getStaticProps: GetStaticProps = async ({
    locale,
    params
}: GetStaticPropsContext) => {
    // category adds .html postfix
    const productUrlWithPostfix = params!.product_url as string // NOSONAR
    const productUrl = productUrlWithPostfix
    try {
        const product = await getProduct(productUrl, locale || '')
        if (!product || !product?.productDetail?.items.length) {
            return {
                notFound: true,
                revalidate: 1
            }
        }

        const cmsBlockResponse = await graphqlFetch<
            { identifiers: string[] },
            { cmsBlocks: CmsBlocks }
        >({
            query: getCMSBlocks,
            variables: {
                identifiers: [PDP_RETURN_POLICY]
            },
            locale
        })
        const returnPolicyBlock = cmsBlockResponse?.data?.cmsBlocks || null

        return {
            props: {
                url_key: productUrl,
                productData: product,
                returnPolicyBlock,
                ...(await serverSideTranslations(locale!, [
                    'common',
                    'pdp',
                    'wishlist'
                ]))
            },
            revalidate: 14400
        }
    } catch (e) {
        console.log('PDP GETSTATICPROPS ERROR')
        console.log(e)
        return {
            props: {
                url_key: productUrl,
                returnPolicyBlock: null,
                ...(await serverSideTranslations(locale!, [
                    'common',
                    'wishlist'
                ]))
            },
            revalidate: 14400
        }
    }
}
const WrappedProductItem = ({
    product
}: {
    product: SimpleProduct | ConfigurableProduct
}): JSX.Element => {
    return (
        <div key={product.id}>
            <ProductItem product={product} isPDP imageParam={{ width: 500 }} />
        </div>
    )
}
export default function PDP({
    url_key,
    productData,
    returnPolicyBlock
}: InferGetStaticPropsType<typeof getStaticProps>): ReactElement {
    const isArabic = useIsArabic()
    const router = useRouter()
    const { t } = useTranslation(['common', 'pdp'])
    const { openToast } = useUI()
    const { customer } = useUser()
    const { addToCart, isAdding } = useAddToCart()
    const config = getStoreConfig(router.locale)
    const { items: returnPolicyItems } = useCmsBlocks([PDP_RETURN_POLICY], {
        initialData: returnPolicyBlock,
        revalidateOnMount: true,
        revalidateOnFocus: false
    })
    const [isMultiBuy, setIsMultiBuy] = useState<boolean>(false)
    const reviewRef = useRef<HTMLDivElement>(null)
    const typeRef = useRef<HTMLDivElement>(null)
    const addTocartRef = useRef<HTMLDivElement>(null)
    const { setScrollToReview, scrollToReview } = useAlfakherContext()
    const inViewport = useIntersection(addTocartRef, '0px')
    const [orderMode, setOrderMode] = useState<number | string | boolean>(false)
    const [billingPeriod, setBillingPeriod] = useState<
        number | string | boolean
    >()
    const [copiedToClipboard, setCopiedToClipboard] = useState(false)
    const [bundlePrice, setSelectedBundlePrice] = useState<{
        price: number
        discountPrice: number
    } | null>(null)
    const isMobile = useIsMobile()
    useEffect(() => {
        if (scrollToReview) {
            window.scrollTo(0, reviewRef?.current?.offsetTop!)
            setScrollToReview(false)
        }
    }, [url_key])
    const {
        product: CurrentProduct,
        isSupportedProductType,
        isConfig,
        isBundle,
        isSimple,
        isGiftCard,
        isValidating,
        error
    } = useProductFetch(
        { productUrl: url_key },
        {
            revalidateOnFocus: false,
            revalidateOnMount: true,
            initialData: { data: productData }
        }
    )
    const product = CurrentProduct
    const review = useMemo(() => {
        if (config.base.yotpo.enabled) {
            return YotpoReviewsAPI({
                id: product?.id
            })
        }
        return null
    }, [])
    const {
        setSelectedOptions,
        selectedOptions,
        setQuantity,
        quantity,
        options,
        variant,
        mediaGalleryEntries,
        buttonLabel: buttonLabelWithoutAdding,
        isButtonDisabled,
        isConfigOptionSelected,
        selectedOptionUIDs,
        selectedBundleProducts,
        setSelectedBundleProducts
    } = useProductUI(product)

    const showAddToCartErrorMessage = (response: CartData | undefined) => {
        const errorMessages = response?.errors ?? response?.user_errors ?? null
        const errorCount = errorMessages?.length

        if (!errorCount) {
            const item = buildAddToCartItemFromProductVariant({
                product,
                variant,
                quantity,
                url: `${config.base.url.baseUrl}p/${product?.url_key}`
            })

            if (customer?.email) {
                trackAddedToCartFromItems([item], { email: customer.email })
            }
        }

        const allErrors = errorMessages
            ? errorMessages.map((errorMessage, i) => (
                  <>
                      <li key={i}>{errorMessage?.message}</li>
                      {errorCount !== i + 1 && <br />}
                  </>
              ))
            : ''
        if (allErrors) {
            openToast(<ul>{allErrors}</ul>, ToastType.Warning, true)
        }
    }
    const addProductHandler = async (multiBuySkus: string[] | null = null) => {
        let addToCartItems: AddToCartItems = [
            {
                sku: product.sku,
                selected_options: selectedOptionUIDs,
                quantity
            }
        ]
        if (orderMode) {
            const addToCartItems = [
                {
                    sku: product.sku,
                    selected_options: selectedOptionUIDs,
                    quantity,
                    end_type: MD_INFINITE
                }
            ]
            // 'variant' of a SimpleProduct is the same as 'product'
            const response = await addToCart(addToCartItems, [product], [])
            return showAddToCartErrorMessage(response)
        }
        if (multiBuySkus) {
            const isParentProduct = multiBuySkus.includes(product.sku)
            setIsMultiBuy(true)
            if (!isParentProduct) addToCartItems = []
            multiBuySkus.forEach((_sku) => {
                if (_sku !== product.sku) {
                    addToCartItems.push({
                        sku: _sku,
                        parent_sku: product.sku,
                        quantity: 1
                    })
                }
            })
        }
        // 'variant' of a SimpleProduct is the same as 'product'
        const response = await addToCart(addToCartItems, [product], [])
        if (multiBuySkus) {
            setIsMultiBuy(false)
        }
        showAddToCartErrorMessage(response)
    }
    const buttonLabel =
        isAdding && !isMultiBuy ? t('Adding...') : t(buttonLabelWithoutAdding)
    usePdpTracking(product, isValidating)
    //route fallback
    if (router.isFallback) {
        return (
            <Container clean className={s['container-classes']}>
                <div>
                    {t('Page for this product was not generated beforehand.')}
                    <br />
                    {t('Loading...')}
                </div>
            </Container>
        )
    }
    if (!product) {
        return <NotFound identifier={config.base.route?.cms_no_route!} />
    }
    //Error handler for PDP page
    if (!product && error) {
        return (
            <Container clean className={s['container-classes']}>
                <h1>{t('Error!!!')}</h1>
            </Container>
        )
    }
    //Warning if the product type is not supported
    if (!isSupportedProductType) {
        return (
            <Container clean className={s['container-classes']}>
                <h1>{`${product?.__typename} ${t('is not supported')}`}</h1>
            </Container>
        )
    }
    const {
        name,
        short_description = { html: '' },
        description,
        faq,
        assembly_care,
        whats_in_box,
        video,
        marketing_block,
        reward_message,
        upsell_label,
        subtitle,
        shipping_return,
        price_range: productPriceRange,
        price_tiers: productPriceTier,
        description_title,
        whats_in_box_title,
        assembly_care_title,
        faq_title
    } = product
    const {
        sku,
        price_promo_text,
        price_range: variantPriceRange,
        price_tiers: variantPriceTier
    } = variant
    const getGalleryImages = (
        mediaGalleryEntriesData: Maybe<MediaGalleryInterface>[]
    ) => {
        if (mediaGalleryEntriesData.length) {
            return [...mediaGalleryEntriesData]
        }
        return mediaGalleryEntriesData
    }
    const hasContents = (content: string) => {
        if (content) {
            const updatedData = content.replace(/<style>(.*?)<\/style>/g, '')
            return (
                updatedData.replace(/<div .*?>|<\/div>/g, '').trim().length > 0
            )
        }
        return false
    }
    const getTierPrice = () => {
        return variantPriceTier.length || productPriceTier.length ? (
            <div className={s['pdp-tier-price']}>
                <ProductTierPrice
                    tierPrice={variantPriceTier || productPriceTier}
                />
            </div>
        ) : (
            ''
        )
    }
    const renderSelector = () => {
        if (isConfig) {
            return (
                <div className={cn('pdp-swatch-wrap', 'pdp-row')}>
                    <VariantSelector
                        disableAll={
                            (product as ConfigurableProduct).stock_status ===
                            ProductStockStatus.OutOfStock
                        }
                        variants={product.variants as ConfigurableVariant[]}
                        options={options}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        displaySwatchOptionLabel
                    />
                </div>
            )
        }
    }
    const cross_shell_title = t('YOU MIGHT ALSO LIKE')
    const renderAddToCartButton = () => {
        const isBundleHiddenOptions =
            isBundle && product?.is_bundle_options_hidden

        const out_of_stock = product.stock_status == 'OUT_OF_STOCK'

        let isATCButtonDisabled: boolean
        if (out_of_stock) {
            isATCButtonDisabled = true
        } else if (!inViewport) {
            isATCButtonDisabled = isAdding
        } else if (isBundleHiddenOptions) {
            isATCButtonDisabled = false
        } else {
            isATCButtonDisabled = isButtonDisabled
        }
        const getButtonLabel = () => {
            if (out_of_stock) {
                return t('SOLD OUT')
            }
            if (isAdding) return t('Adding...')
            if (isBundleHiddenOptions) return t(ButtonLabel.ADD_TO_CART)
            if (inViewport) return buttonLabel
            if (isSimple) return t(ButtonLabel.ADD_TO_CART)
            return t(ButtonLabel.BUY_NOW)
        }
        const onButtonClick = () => {
            if (!inViewport && isButtonDisabled) {
                addTocartRef.current?.scrollIntoView({
                    block: 'start',
                    behavior: 'smooth'
                })
            } else {
                addProductHandler()
            }
        }
        return (
            <Button
                aria-label={t('Add to Cart')}
                type="button"
                className={cn(s['add-to-cart-button'], {
                    'add-to-cart-button-floating': !inViewport
                })}
                onClick={() => onButtonClick()}
                disabled={isATCButtonDisabled}
                variant="primary"
            >
                {getButtonLabel()}
            </Button>
        )
    }
    const copyToClipboard = () => {
        const currentURL = window.location.href
        navigator.clipboard
            .writeText(currentURL)
            .then(() => {
                setCopiedToClipboard(true)
                setTimeout(() => setCopiedToClipboard(false), 2000)
            })
            .catch((err) => {
                console.error('Unable to copy URL to clipboard', err)
            })
    }

    if (isBundle && product?.is_bundle_options_hidden) {
        product.items?.forEach((item: { options?: { uid?: string }[] }) => {
            if (item?.options && item.options.length > 0) {
                selectedOptionUIDs.push(item.options[0]?.uid as string)
            }
        })
    }

    const onCustomizeButtonClick = () => {
        if (!router?.query?.option?.includes('customize')) {
            router.push({
                pathname: router.pathname,
                query: { ...router.query, option: 'customize' }
            })
        }
    }
    const renderProductByTypes = () => {
        if (isGiftCard) {
            return (
                <div
                    ref={addTocartRef}
                    className={s['selector-with-add-to-cart']}
                >
                    <GiftCardProduct product={product} />
                </div>
            )
        }
        const bundleRenderer =
            !product.is_custom_kit && !product.is_bundle_options_hidden ? (
                <BundleProductSelector
                    bundleProduct={product}
                    selectedBundleProducts={selectedBundleProducts}
                    setSelectedBundleProducts={setSelectedBundleProducts}
                    setSelectedBundlePrice={setSelectedBundlePrice}
                    showAllSelected={false}
                />
            ) : (
                ''
            )
        return (
            <>
                <div className={s['pdp-price']}>
                    {price_promo_text && (
                        <div className={s['product-promo-text']}>
                            {price_promo_text}
                        </div>
                    )}
                    <div className={s['pdp-price-block']}>
                        <ProductPrice
                            priceText
                            priceRange={variantPriceRange || productPriceRange}
                            productType={variant.__typename}
                            bundlePrice={bundlePrice}
                            showDiscountPrice={!product?.is_bulk_product}
                        />
                    </div>
                </div>
                <PaymentOptionsWidget
                    price={
                        (variantPriceRange || productPriceRange)?.minimum_price
                            ?.final_price
                    }
                />
                <div className={s['product-gallery-mobile-wrapper']}>
                    {renderProductGallery()}
                </div>
                {getTierPrice()}
                <div
                    ref={addTocartRef}
                    className={s['selector-with-add-to-cart']}
                >
                    {isBundle ? (
                        bundleRenderer
                    ) : (
                        <>
                            {getTierPrice()}
                            {renderSelector()}
                        </>
                    )}
                    {!product.is_custom_kit ? (
                        <div
                            className={cn(
                                s['action-wrapper'],
                                'action-wrapper'
                            )}
                        >
                            {product.stock_status !== 'OUT_OF_STOCK' && (
                                <div className={s['qty-selector']}>
                                    <QuantitySelector
                                        quantity={quantity}
                                        setQuantity={setQuantity}
                                    />
                                </div>
                            )}
                            {renderAddToCartButton()}
                        </div>
                    ) : (
                        <Button
                            aria-label={t('Add to Cart')}
                            type="button"
                            className={cn(s['add-to-cart-button'], {
                                // 'add-to-cart-button-floating': !inViewport
                            })}
                            onClick={() => onCustomizeButtonClick()}
                            disabled={product.stock_status === 'OUT_OF_STOCK'}
                            variant="primary"
                            // eslint-disable-next-line i18next/no-literal-string
                        >
                            {product.stock_status === 'OUT_OF_STOCK'
                                ? t('SOLD OUT')
                                : t('customize')}
                        </Button>
                    )}
                </div>
            </>
        )
    }
    const renderProductGallery = () => {
        return (
            <div className={s['product-gallery']}>
                {variant.media_gallery.length ? (
                    <ProductGallery
                        mediaGalleryEntries={getGalleryImages(
                            mediaGalleryEntries
                        )}
                        config={{
                            mobile: {
                                gallerytype: GalleryTypeEnum.SCROLL,
                                zoom: false,
                                thumb: false,
                                fullscreen: true,
                                sliderProps: {
                                    main: {
                                        spaceBetween: 10,
                                        pagination: {
                                            clickable: true
                                        }
                                    }
                                }
                            },
                            desktop: {
                                gallerytype: GalleryTypeEnum.SCROLL,
                                zoom: false,
                                thumb: true,
                                fullscreen: true,
                                sliderProps: {
                                    main: {
                                        spaceBetween: 10,
                                        pagination: false
                                    },
                                    thumbs: {
                                        spaceBetween: 20,
                                        slidesPerView: 3
                                    }
                                }
                            }
                        }}
                    />
                ) : (
                    <Image
                        alt={t(product?.name)}
                        src={config?.seo?.product?.image?.placeholder || ''}
                        objectFit="contain"
                        width={900}
                        height={900}
                    />
                )}
                <div className={s['pdp-mp-badges']}>
                    {product.mp_label_data && (
                        <MagePlazaProductBadge
                            labelData={product.mp_label_data}
                            showAllCustom
                        />
                    )}
                </div>
            </div>
        )
    }
    if (
        isBundle &&
        (router.query.option?.includes('customize') ||
            router.asPath.includes('option=customize'))
    ) {
        return (
            <Container clean>
                <div ref={typeRef}>
                    <YotpoScripts
                        enabled={config.base.yotpo.enabled}
                        apiKey={config.base.yotpo.api_key}
                    />
                    <KlaviyoViewedProductTracker
                        product={product}
                        activeProductSKU={sku}
                    />
                    <SEO
                        product={yotpoReviewsJsonLd({ product, review })}
                        page={SEO_ENUMS.PDP}
                    />
                    <CustomKitLayout
                        product={product}
                        bundlePrice={bundlePrice}
                        setSelectedBundlePrice={setSelectedBundlePrice}
                        setOrderMode={setOrderMode}
                        orderMode={orderMode}
                        showAddToCartErrorMessage={showAddToCartErrorMessage}
                        isMultiBuy={isMultiBuy}
                        setIsMultiBuy={setIsMultiBuy}
                        isLoading={isValidating}
                    />
                </div>
            </Container>
        )
    }
    return (
        <Container clean>
            <div ref={typeRef}>
                <YotpoScripts
                    id={product.id}
                    enabled={config.base.yotpo.enabled}
                    apiKey={config.base.yotpo.api_key}
                />
                <KlaviyoViewedProductTracker
                    product={product}
                    activeProductSKU={sku}
                />
                <div className={s['breadcrumb-wrapper']}>
                    <div className={cn(s['pdp-breadcrumbs'], 'breadcrumbs')}>
                        {product.categories?.length ? (
                            <Breadcrumbs
                                product={product}
                                separator=" / "
                                urlPrefix={t('/c')}
                            />
                        ) : null}
                    </div>
                </div>
                <div className={cn(s['container-classes'])}>
                    <SEO
                        product={yotpoReviewsJsonLd({ product, review })}
                        page={SEO_ENUMS.PDP}
                    />
                    <div
                        className={cn(
                            s['pdp-container'],
                            !inViewport && 'showBuyButton'
                        )}
                    >
                        {renderProductGallery()}
                        <div className={s['product-main']}>
                            <div className={s['product-details']}>
                                <div>
                                    {product.brand_text !== 'false' && (
                                        <div className={s['product-sku']}>
                                            {product.brand_text}
                                        </div>
                                    )}
                                    <div className={s['main-product-header']}>
                                        <div>
                                            <h1 className={s['product-name']}>
                                                {name}
                                            </h1>
                                        </div>
                                        <div
                                            className={cn(
                                                s['wishlist-btn-container']
                                            )}
                                        >
                                            <div
                                                className={cn({
                                                    'hide-btn-wishlist':
                                                        isConfigOptionSelected ||
                                                        isButtonDisabled
                                                })}
                                            >
                                                <AddToWishlist
                                                    sku={product?.sku}
                                                    selected_options={
                                                        selectedOptionUIDs
                                                    }
                                                    quantity={1}
                                                    isWishlistDisabled={
                                                        isConfigOptionSelected ||
                                                        isButtonDisabled
                                                    }
                                                    isConfig={isConfig}
                                                    isVariantOOS={
                                                        variant.stock_status !==
                                                        ProductStockStatus.InStock
                                                    }
                                                />
                                            </div>
                                            <div
                                                className={s['copy-icon']}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        copyToClipboard()
                                                    }
                                                }}
                                                onClick={() =>
                                                    copyToClipboard()
                                                }
                                            >
                                                <ShareIcon />
                                                {copiedToClipboard && (
                                                    <div
                                                        className={
                                                            s['copied-text']
                                                        }
                                                    >
                                                        {t('Copied')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={s['product-subtitle']}>
                                        {subtitle}
                                    </div>
                                </div>
                                <div className={s['product-topinfo']}>
                                    <div
                                        className={s['sku']}
                                        tabIndex={0}
                                        role="button"
                                        aria-label={sku}
                                    >
                                        {t('SKU: ')}
                                        {sku}
                                    </div>
                                    <div className={s['rating']}>
                                        <YotpoStarRating
                                            enabled={config.base.yotpo.enabled}
                                            productId={product?.id} // NOSONAR
                                        />
                                    </div>
                                </div>
                                {isMobile ? (
                                    <ReadMore
                                        content={short_description?.html}
                                    />
                                ) : (
                                    <RichContent
                                        html={product.short_description?.html}
                                    />
                                )}
                                {renderProductByTypes()}
                                <div className={s['product-info']}>
                                    <ProductInfo
                                        description={description}
                                        assemblyCare={assembly_care}
                                        whatsInBox={whats_in_box}
                                        faq={faq}
                                        descriptionTitle={description_title}
                                        whatsInBoxTitle={whats_in_box_title}
                                        assemblyCareTitle={assembly_care_title}
                                        faqTitle={faq_title}
                                        returnPolicy={
                                            returnPolicyItems?.[0]?.content
                                        }
                                        returnPolicyTitle={
                                            returnPolicyItems?.[0]?.title
                                        }
                                    />
                                </div>
                                {hasContents(shipping_return) && (
                                    <div className={s['pdp-shipping-return']}>
                                        <CollapsibleContainer
                                            title={t('Shipping and Return')}
                                            iconClose={false}
                                            iconOpen={false}
                                            classes={
                                                s['pdp-collapsible-container']
                                            }
                                        >
                                            <RichContent
                                                html={shipping_return}
                                            />
                                        </CollapsibleContainer>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <MultiBuyProducts
                    product={product}
                    variant={variant}
                    title={product.multi_buy_label}
                    addProductsToCart={addProductHandler}
                    isAdding={isAdding && isMultiBuy}
                    isButtonDisabled={isButtonDisabled}
                    mainButtonLabel={buttonLabelWithoutAdding}
                />
                <div
                    className={cn(s['pdp-btm-content-block'], slickStyles.root)}
                >
                    <CrossShell
                        className={s['upsell-slider']}
                        ProductTile={WrappedProductItem}
                        title={cross_shell_title}
                        products={product?.crosssell_products}
                        sliderSettings={{
                            arrows: true,
                            speed: 400,
                            slidesToScroll: 1,
                            slidesToShow: 4,
                            draggable: true,
                            touchMove: true,
                            infinite: false,
                            dots: false,
                            fade: false,
                            prevArrow: (
                                <button type="button" className="slick-prev">
                                    <SlickprevCircle />
                                </button>
                            ),
                            nextArrow: (
                                <button type="button" className="slick-next">
                                    <SlicknextCircle />
                                </button>
                            ),
                            responsive: [
                                {
                                    breakpoint: 1023,
                                    settings: {
                                        slidesToScroll: 2,
                                        slidesToShow: 3
                                    }
                                },
                                {
                                    breakpoint: 767,
                                    settings: {
                                        slidesToScroll: 1,
                                        slidesToShow: 1.06,
                                        arrows: false,
                                        dots: false
                                    }
                                },
                                {
                                    breakpoint: 480,
                                    settings: {
                                        slidesToScroll: 1,
                                        slidesToShow: 1.06,
                                        arrows: false,
                                        dots: false
                                    }
                                }
                            ]
                        }}
                    />
                </div>
                {hasContents(marketing_block) && (
                    <div>
                        <div className={s['product-marketing']}>
                            <RichContent
                                html={marketing_block}
                                ProductTile={WrappedProductItem}
                            />
                        </div>
                        <div className={s['product-video']}>
                            <RichContent html={video} />
                        </div>
                    </div>
                )}
            </div>
            <div className={cn(s['pdp-btm-content-block'], slickStyles.root)}>
                <Upsell
                    className={s['upsell-slider']}
                    ProductTile={WrappedProductItem}
                    title={upsell_label}
                    products={product?.upsell_products}
                    sliderSettings={{
                        arrows: true,
                        speed: 400,
                        slidesToScroll: 1,
                        slidesToShow: 4,
                        draggable: true,
                        touchMove: true,
                        infinite: false,
                        dots: false,
                        fade: false,
                        rtl: isArabic,
                        prevArrow: (
                            <button type="button" className="slick-prev">
                                <SlickprevCircle />
                            </button>
                        ),
                        nextArrow: (
                            <button type="button" className="slick-next">
                                <SlicknextCircle />
                            </button>
                        ),
                        responsive: [
                            {
                                breakpoint: 1023,
                                settings: {
                                    slidesToScroll: 2,
                                    slidesToShow: 3
                                }
                            },
                            {
                                breakpoint: 767,
                                settings: {
                                    slidesToScroll: 1,
                                    slidesToShow: 1.06,
                                    arrows: false,
                                    dots: false
                                }
                            },
                            {
                                breakpoint: 480,
                                settings: {
                                    slidesToScroll: 1,
                                    slidesToShow: 1.06,
                                    arrows: false,
                                    dots: false
                                }
                            }
                        ]
                    }}
                />
            </div>
            <div
                ref={reviewRef}
                className={cn(s['yotpo-review'], 'yotpo-review')}
            >
                <div className={s['yotpo-review-container']}>
                    <h2 className={s['yotpo-review-title']}>
                        {t('CUSTOMER REVIEW')}
                    </h2>
                </div>
                <YotpoReviews
                    enabled={config.base.yotpo.enabled}
                    productId={product?.id} // NOSONAR
                    price={product?.minimum_price?.final_price?.value}
                    currency={product?.minimum_price?.final_price?.currency}
                    name={product?.name}
                    url={`${config.base.url.baseUrl}p/${product?.url_key}`}
                    imageUrl={product?.small_image?.url}
                    description=""
                />
            </div>
        </Container>
    )
}
PDP.Layout = PdpLayout
